import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Box,
  Typography,
  Divider,
  TextField,
  FormControlLabel,
  Checkbox,
  MenuItem,
} from '@mui/material';
import {
  useGetMany,
  useGetManyReference,
  useGetOne,
  useNotify,
  useRefresh,
  useRedirect,
} from 'react-admin';
import { Contract } from '../types';
import { getToken, getRefreshToken } from '../authentication/authProvider';
import FruitionPrimaryPicker, { FruitionPrimaryChoice } from './FruitionPrimaryPicker';

type ConvertPreneedDialogProps = {
  open: boolean;
  onClose: () => void;
  contract: Contract;
};

type LineItem = {
  item: any;
  description?: string;
  price: number;
  quantity: number;
  tax?: string | null;
  total?: number;
};

const normalizeLines = (lines: any[] = []): LineItem[] =>
  lines.map((line) => ({
    ...line,
    price: Number(line.price) || 0,
    quantity: Number(line.quantity) || 0,
    total: line.total ?? (Number(line.price) || 0) * (Number(line.quantity) || 0),
  }));

const calculateLinesTotal = (lines: LineItem[]) =>
  lines.reduce((sum, line) => sum + (line.price || 0) * (line.quantity || 0), 0);

const ConvertPreneedDialog = ({ open, onClose, contract }: ConvertPreneedDialogProps) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const redirect = useRedirect();

  const [lineItems, setLineItems] = useState<LineItem[]>(() => normalizeLines(contract.lines || []));
  const [sendAgreements, setSendAgreements] = useState(false);
  const [agreementType, setAgreementType] = useState<string>('cleo');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [agreementRecipientId, setAgreementRecipientId] = useState<string>('');
  const [agreementRecipientName, setAgreementRecipientName] = useState('');
  const [agreementRecipientEmail, setAgreementRecipientEmail] = useState('');
  const [notes, setNotes] = useState('');
  // T-210: the planner has died; fruition needs a new family main contact.
  const [primaryChoice, setPrimaryChoice] = useState<FruitionPrimaryChoice | null>(null);

  const defaultPaymentAmount = useMemo(() => {
    if (contract.total_amount != null) {
      return (contract.total_amount / 100).toFixed(2);
    }
    const total = calculateLinesTotal(normalizeLines(contract.lines || []));
    return total.toFixed(2);
  }, [contract]);

  const [paymentAmount, setPaymentAmount] = useState(defaultPaymentAmount);
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);

  const { data: funeral } = useGetOne(
    'funerals',
    { id: contract.funeral_id },
    { enabled: !!contract.funeral_id }
  );

  const itemIds = useMemo(() => {
    const ids = new Set<string>();
    lineItems.forEach((line) => {
      if (line.item != null) {
        ids.add(String(line.item));
      }
    });
    return Array.from(ids);
  }, [lineItems]);

  const { data: items } = useGetMany(
    'items',
    { ids: itemIds },
    { enabled: itemIds.length > 0 }
  );

  const itemNameById = useMemo(() => {
    const map = new Map<string, string>();
    (items || []).forEach((item: any) => {
      const id = String(item.id ?? item.Id ?? '');
      if (id) {
        map.set(id, item.FullyQualifiedName || item.Name || id);
      }
    });
    return map;
  }, [items]);

  const { data: funeralClients } = useGetManyReference(
    'funeral_clients',
    { target: 'funeral_id', id: funeral?.id },
    { enabled: !!funeral?.id }
  );

  const clientIds = useMemo(() => {
    const ids = new Set<number>();
    if (funeral?.primary_client_id) ids.add(funeral.primary_client_id);
    if (funeral?.paying_client_id) ids.add(funeral.paying_client_id);
    if (funeral?.authorized_client_id) ids.add(funeral.authorized_client_id);
    (funeralClients || []).forEach((fc: any) => {
      if (fc?.client_id) ids.add(fc.client_id);
    });
    return Array.from(ids);
  }, [funeral, funeralClients]);

  const { data: clients } = useGetMany(
    'clients',
    { ids: clientIds },
    { enabled: clientIds.length > 0 }
  );

  useEffect(() => {
    setLineItems(normalizeLines(contract.lines || []));
  }, [contract]);

  useEffect(() => {
    if (funeral?.authorized_client_id) {
      setAgreementRecipientId(String(funeral.authorized_client_id));
    }
  }, [funeral?.authorized_client_id]);

  useEffect(() => {
    if (funeral?.region === 'quebec') {
      setAgreementType('rideau');
    } else if (funeral?.region === 'ontario') {
      setAgreementType('southwest');
    }
  }, [funeral?.region]);

  useEffect(() => {
    if (!sendAgreements) {
      return;
    }

    if (!agreementRecipientId) {
      if (funeral?.authorized_client_id) {
        setAgreementRecipientId(String(funeral.authorized_client_id));
      } else if (clients && clients.length > 0) {
        setAgreementRecipientId(String(clients[0].id));
      }
      return;
    }

    if (agreementRecipientId === 'other') {
      setAgreementRecipientName('');
      setAgreementRecipientEmail('');
      return;
    }

    if (agreementRecipientId) {
      const selectedClient = (clients || []).find(
        (client: any) => String(client.id) === agreementRecipientId
      );
      if (selectedClient) {
        setAgreementRecipientName(selectedClient.name || '');
        setAgreementRecipientEmail(selectedClient.email || '');
      }
    }
  }, [agreementRecipientId, clients, sendAgreements]);

  const handleSubmit = async () => {
    try {
      if (isSubmitting) {
        return;
      }
      if (!primaryChoice) {
        notify('Choose the new primary contact first', { type: 'warning' });
        return;
      }
      setIsSubmitting(true);
      const amountValue = Number(paymentAmount || 0);
      if (!amountValue || amountValue <= 0) {
        notify('Partner payment amount must be greater than 0', { type: 'warning' });
        setIsSubmitting(false);
        return;
      }

      if (sendAgreements) {
        if (!agreementRecipientName || !agreementRecipientEmail) {
          notify('Agreement recipient name and email are required', { type: 'warning' });
          setIsSubmitting(false);
          return;
        }
      }

      const token = await getToken();
      const refreshToken = await getRefreshToken();

      const payload: any = {
        sendAgreements,
        agreementType: sendAgreements ? agreementType : undefined,
        partnerPaymentAmount: Math.round(amountValue * 100),
        partnerPaymentDate: paymentDate || undefined,
        notes: notes || undefined,
        ...primaryChoice,
      };

      if (sendAgreements) {
        if (agreementRecipientId && agreementRecipientId !== 'other') {
          payload.agreementRecipientClientId = Number(agreementRecipientId);
        }
        if (agreementRecipientName) {
          payload.agreementRecipientName = agreementRecipientName;
        }
        if (agreementRecipientEmail) {
          payload.agreementRecipientEmail = agreementRecipientEmail;
        }
      }

      const response = await fetch(
        `${process.env.REACT_APP_PAPERMAN_API_URL}contract/fulfill-preneed/${contract.id}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            'Refresh-Token': refreshToken,
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to convert preneed contract');
      }

      notify('Preneed converted to at-need successfully', { type: 'success' });
      refresh();
      if (contract?.funeral_id) {
        redirect('show', 'funerals', contract.funeral_id);
      }
      onClose();
    } catch (error: any) {
      notify(error?.message || 'Failed to convert preneed contract', { type: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const showLegacyLinesWarning = contract.legacy_lines?.lines?.length > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Fruition Preneed Contract #{contract.id}</DialogTitle>
      <DialogContent>
        <Box>
          <FruitionPrimaryPicker funeral={funeral} onChange={setPrimaryChoice} />

          <Divider sx={{ my: 2 }} />

          <Typography variant="h6" sx={{ mb: 1 }}>
            Contract Details
          </Typography>
          {showLegacyLinesWarning && (
            <Typography variant="body2" color="warning.main" sx={{ mb: 1 }}>
              Legacy line items are shown for reference only.
            </Typography>
          )}

          <Box sx={{ mt: 1 }}>
            {lineItems.length === 0 ? (
              <Typography variant="body2">No line items available.</Typography>
            ) : (
              lineItems.map((line, index) => (
                <Box
                  key={`${line.item}-${index}`}
                  sx={{ display: 'flex', gap: 2, mb: 1, alignItems: 'center' }}
                >
                  <TextField
                    label="Item"
                    value={
                      line.description ||
                      itemNameById.get(String(line.item)) ||
                      line.item?.Name ||
                      line.item ||
                      ''
                    }
                    size="small"
                    sx={{ minWidth: 220 }}
                    InputProps={{ readOnly: true }}
                  />
                  <TextField
                    label="Description"
                    value={line.description || ''}
                    size="small"
                    sx={{ minWidth: 260 }}
                    InputProps={{ readOnly: true }}
                  />
                  <TextField
                    label="Price"
                    type="number"
                    size="small"
                    sx={{ width: 120 }}
                    value={line.price ?? 0}
                    InputProps={{ readOnly: true }}
                  />
                  <TextField
                    label="Qty"
                    type="number"
                    size="small"
                    sx={{ width: 90 }}
                    value={line.quantity ?? 0}
                    InputProps={{ readOnly: true }}
                  />
                  <TextField
                    label="Total"
                    size="small"
                    sx={{ width: 120 }}
                    value={((line.price || 0) * (line.quantity || 0)).toFixed(2)}
                    InputProps={{ readOnly: true }}
                  />
                </Box>
              ))
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography variant="h6" sx={{ mb: 1 }}>
            Payment Information
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Partner payment amount (CAD)"
              type="number"
              value={paymentAmount}
              onChange={(event) => setPaymentAmount(event.target.value)}
              size="small"
              sx={{ maxWidth: 240 }}
            />
            <TextField
              label="Payment date"
              type="date"
              value={paymentDate}
              onChange={(event) => setPaymentDate(event.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <TextField
            label="Notes"
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            fullWidth
            multiline
            minRows={2}
            sx={{ mt: 2 }}
          />

          <Divider sx={{ my: 2 }} />

          <Typography variant="h6" sx={{ mb: 1 }}>
            Cremation Agreement
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={sendAgreements}
                onChange={(event) => setSendAgreements(event.target.checked)}
              />
            }
            label="Send cremation agreement"
          />

          {sendAgreements && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <TextField
                  select
                  label="Agreement type"
                  size="small"
                  value={agreementType}
                  onChange={(event) => setAgreementType(event.target.value)}
                  sx={{ minWidth: 170 }}
                >
                  <MenuItem value="rideau">Rideau</MenuItem>
                  <MenuItem value="southwest">Southwest</MenuItem>
                  <MenuItem value="cleo">Cleo Agreements</MenuItem>
                </TextField>
              </Box>
              <TextField
                select
                label="Send to"
                value={agreementRecipientId}
                onChange={(event) => setAgreementRecipientId(event.target.value)}
                size="small"
                sx={{ minWidth: 320 }}
              >
                {(clients || []).map((client: any) => (
                  <MenuItem key={client.id} value={String(client.id)}>
                    {client.name}
                  </MenuItem>
                ))}
                <MenuItem value="other">Someone else</MenuItem>
              </TextField>

              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <TextField
                  label="Name"
                  value={agreementRecipientName}
                  onChange={(event) => setAgreementRecipientName(event.target.value)}
                  size="small"
                  sx={{ minWidth: 260 }}
                />
                <TextField
                  label="Email"
                  value={agreementRecipientEmail}
                  onChange={(event) => setAgreementRecipientEmail(event.target.value)}
                  size="small"
                  sx={{ minWidth: 260 }}
                />
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => void handleSubmit()}
          disabled={isSubmitting || !primaryChoice}
        >
          Mark as Fulfilled
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConvertPreneedDialog;
